import { AppHeader } from '@/components/chat/AppHeader';
import { ChatLayoutContext } from '@/components/context/ChatLayoutContext';
import { closeWidget, getCurrentOpenWidgetIds } from '@/store/reducers/UIState';
import { DataVoyager } from '@/components/data-voyager/DataVoyager';
import { PaperFinder } from '@/components/paper-finder/PaperFinder';
import { Reader } from '@/components/reader/Reader';
import { Report } from '@/components/report/Report';
import { scrollbarStyles } from '@/components/shared';
import { Table } from '@/components/table/Table';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { WidgetType } from '@/models/PanelWidgets';

import {
  Box,
  Card,
  Divider,
  Drawer,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { Fragment, ReactNode, useContext, useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styled from 'styled-components';

export const WidgetPanel = (): ReactNode => {
  const dispatch = useAppDispatch();
  const { setIsChatDrawerOpen } = useContext(ChatLayoutContext);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    // shut the nav drawer before opening the widget panel so it doesn't cover the panel
    setIsChatDrawerOpen(false);
    setIsDrawerOpen(isSmallScreen);
  }, [isSmallScreen]);

  const openWidgetIds = useAppSelector(getCurrentOpenWidgetIds);

  const renderWidget = (widgetId: string): ReactNode => {
    // widgetId is in the format of widgetType-uniqueId
    // use the action creators in WidgetActions to dispatch opening a widget type
    // to ensure the widgetId is auto generated in the correct format
    const widgetType = widgetId.split('-')[0];
    switch (widgetType) {
      case WidgetType.READER:
        // to display multiple instances of a widget in the future, we can pass the widgetId down
        // to the widget and have it look up the data by widgetId in the respective widget store
        return <Reader key={widgetId} />;
      case WidgetType.TABLE:
        return <Table key={widgetId} />;
      case WidgetType.PAPER_FINDER:
        return <PaperFinder key={widgetId} />;
      case WidgetType.REPORT:
        return (
          <Box p={1.5}>
            <Report key={widgetId} />
          </Box>
        );
      case WidgetType.DATA_VOYAGER:
        return <DataVoyager key={widgetId} />;
      default:
        return null;
    }
  };

  const handleClosePanel = (): void => {
    setIsDrawerOpen(false);
    // close the drawer with a slight delay to allow the drawer to close before the panel is removed from the DOM
    setTimeout(() => {
      dispatch(closeWidget());
    }, 300);
  };

  const panel = (
    <Panel id="nora-panel">
      <Scroller>
        <Box sx={{ minHeight: '100%' }}>
          {openWidgetIds.map((id) => renderWidget(id))}
        </Box>
      </Scroller>
    </Panel>
  );

  return (
    <Fragment>
      {panel}
      <StyledDrawer open={isDrawerOpen} anchor="right">
        <AppHeader alwaysShowMenuButton={true}></AppHeader>
        <Divider sx={{ borderColor: 'rgba(250,242,233,0.1)' }} />
        <BackBar>
          <BackButton onClick={handleClosePanel} size="small">
            <ArrowBackIcon />
            Back to chat
          </BackButton>
        </BackBar>

        {panel}
      </StyledDrawer>
    </Fragment>
  );
};

export const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.color['dark-teal-100'].hex,
    border: 'none',
  },

  '& .MuiDrawer-paper': {
    width: '100%',
  },
}));

const Panel = styled(Card)`
  &.MuiCard-root {
    background-color: transparent;
  }
  height: calc(100% - ${({ theme }) => theme.spacing(3 * 2)});
  position: relative;
  width: 100%;
`;

const Scroller = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  position: absolute;
  inset: 0;
  overflow: auto;
  scrollbar-gutter: stable; /* helps reserve space for scrollbar */

  ${scrollbarStyles};
`;

const BackBar = styled(Box)`
  padding: ${({ theme }) => theme.spacing(1)};
`;

const BackButton = styled(IconButton)`
  color: ${({ theme }) => theme.palette.secondary.main};
  font-size: 0.875em;

  & .MuiSvgIcon-root {
    margin-right: ${({ theme }) => theme.spacing(1)};
  }
`;
